import { useState, useEffect, useCallback, useRef } from "react";
import PubSub from "pubsub-js";
import Typography from "aldoo-ra/Typography";
import Button from "aldoo-ra/Button";
import DataTable from "aldoo-ra/DataTable";
import Checkbox from "aldoo-ra/Checkbox";
import Spinner from "aldoo-ra/Spinner";
import { showSuccessToast, TOAST_POSITIONS } from "aldoo-ra/Toast";
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog";
import { openGoPremium } from "../GoPremiumDialog";
import {
  InboxArrowDownIcon,
  PlusIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import TextBundle from "aldoo-ra/CMS/text-bundle";
import { listLinks, setLinkStatus } from "./controller";
import ReceiveLinkDialog from "./ReceiveLinkDialog";
import ReceiveLinkDrawer from "./ReceiveLinkDrawer";
import StatusBadge from "../FileShareDashboard/StatusBadge";
import Tooltip from "aldoo-ra/Tooltip";
import { formatRelativeTime } from "../FileShareDashboard/utils";

// Mirrors the FileShareDashboard "today / yesterday / dd/mm" + time pattern.
function renderDateCell(
  value,
  todayLabel = "Today",
  yesterdayLabel = "Yesterday",
) {
  if (!value) return null;
  const d = new Date(value);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const dateLabel = isToday
    ? todayLabel
    : isYesterday
      ? yesterdayLabel
      : d.toLocaleDateString();
  return (
    <Tooltip text={formatRelativeTime(value)} position="top">
      <div className="flex flex-col text-xs">
        <span>{dateLabel}</span>
        <span className="text-text_secondary">
          {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
    </Tooltip>
  );
}

// Like FileShareDashboard's formatTimeRemaining, but reads from
// link_expires_at rather than Order.expires_at / grace_expires_at.
function linkTimeRemaining(link) {
  if (!link || link.status === "expired") return null;
  const target = new Date(link.link_expires_at);
  const diffMs = target - new Date();
  if (diffMs <= 0) return null;
  const oneDay = 1000 * 60 * 60 * 24;
  if (diffMs >= oneDay) {
    const days = Math.ceil(diffMs / oneDay);
    return {
      value: `${days} day${days !== 1 ? "s" : ""}`,
      suffix: "remaining",
    };
  }
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const totalMins = hours * 60 + minutes;
  if (totalMins >= 60)
    return { value: `${Math.ceil(totalMins / 60)}h`, suffix: "remaining" };
  if (totalMins > 0)
    return {
      value: `${totalMins} min${totalMins !== 1 ? "s" : ""}`,
      suffix: "remaining",
    };
  return { value: "less than a minute", suffix: "remaining" };
}

// Coerce a link into the status vocabulary StatusBadge understands. A link
// whose link_expires_at has passed but is still marked "active" should
// render as expired so the badge color matches reality.
function effectiveStatus(link) {
  if (!link) return "";
  if (
    link.status === "active" &&
    new Date(link.link_expires_at).getTime() <= Date.now()
  ) {
    return "expired";
  }
  return link.status;
}

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ReceiveLinksPanel() {
  const [links, setLinks] = useState([]);
  const [maxLinks, setMaxLinks] = useState(0);
  const [canCreate, setCanCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [drawerLink, setDrawerLink] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const tableWrapperRef = useRef(null);
  const scrollHintRef = useRef(null);

  const positionScrollHint = useCallback(() => {
    const el = scrollHintRef.current;
    const container = tableWrapperRef.current?.querySelector('.overflow-x-auto');
    if (!el || !container) return;
    el.style.top = `${container.offsetTop + 8}px`;
  }, []);

  const texts = TextBundle({ id: "transfer_receive_links" })

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (prev.size === links.length && links.length > 0) return new Set();
      return new Set(links.map((l) => l._id));
    });
  };
  const allSelected = links.length > 0 && selected.size === links.length;
  const someSelected = selected.size > 0;

  const handleDeleteSingle = async (link) => {
    const ok = await showPromptDialogAsync({
      title: texts?.deleteLinkTitle,
      message: texts?.deleteLinkMsg,
      yesLabel: texts?.delete,
      noLabel: texts?.cancel,
    });
    if (!ok) return;
    setIsDeleting(true);
    try {
      const res = await setLinkStatus(link._id, "delete");
      if (res?.result) {
        setLinks((prev) => prev.filter((l) => l._id !== link._id));
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(link._id);
          return next;
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!someSelected || isDeleting) return;
    const ok = await showPromptDialogAsync({
      title: texts?.deleteLinksTitle,
      message: texts?.deleteLinksMsg,
      yesLabel: texts?.delete,
      noLabel: texts?.cancel,
    });
    if (!ok) return;
    setIsDeleting(true);
    try {
      await Promise.all([...selected].map((id) => setLinkStatus(id, "delete")));
      setSelected(new Set());
      load();
    } finally {
      setIsDeleting(false);
    }
  };

  // Reason text surfaced as the GoPremium dialog headline when the user
  // taps a gated "Request Files" / "Upgrade" CTA. Distinguishes the
  // feature-locked state (free plan, no receive-link entitlement) from
  // the cap-reached state so the dialog explains the actual block.
  const upgradeReason = () => {
    if (maxLinks === 0) {
      return texts?.receiveLocked;
    }
    return texts?.receiveLinkCapReached;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listLinks();
      if (res?.result) {
        const fresh = res.links || [];
        setLinks(fresh);
        setMaxLinks(res.maxLinks || 0);
        setCanCreate(!!res.canCreate);
        // Re-point the open drawer at the fresh row — without this an
        // external update (iOS toggle, second tab, etc.) lands in the
        // list but the drawer keeps the stale snapshot, so the
        // toggles read as before the change.
        setDrawerLink((curr) =>
          curr ? fresh.find((l) => l._id === curr._id) || curr : curr,
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime: any change to the owner's receive-links or an inbound
  // transfer through one of them triggers a list reload. Cheap — the
  // list lives in memory client-side.
  useEffect(() => {
    const events = [
      "transfer.receivelink.created",
      "transfer.receivelink.updated",
      "transfer.share.received",
    ];
    const tokens = events.map((e) => PubSub.subscribe(e, () => load()));
    return () => tokens.forEach((t) => PubSub.unsubscribe(t));
  }, [load]);

  useEffect(() => {
    const outer = tableWrapperRef.current;
    const container = outer?.querySelector('.overflow-x-auto');
    if (!container) return;
    positionScrollHint();
    const ro = new ResizeObserver(positionScrollHint);
    ro.observe(outer);
    const check = () => {
      setShowScrollHint(container.scrollLeft + container.clientWidth < container.scrollWidth - 2);
    };
    requestAnimationFrame(check);
    container.addEventListener('scroll', check, { passive: true });
    return () => {
      ro.disconnect();
      container.removeEventListener('scroll', check);
    };
  }, [links.length, positionScrollHint]);

  const buildUrl = (link) => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/r/${link.token}`;
  };

  const copy = (e, link) => {
    e.stopPropagation();
    try {
      navigator.clipboard.writeText(buildUrl(link));
      setCopiedToken(link.token);
      setTimeout(
        () => setCopiedToken((t) => (t === link.token ? null : t)),
        1500,
      );
    } catch {}
  };

  // Push a fresh link back into the list — keeps the drawer + table in sync
  // without a full reload after each tab save.
  const upsertLink = (link) => {
    setLinks((prev) => prev.map((l) => (l._id === link._id ? link : l)));
    setDrawerLink((curr) => (curr && curr._id === link._id ? link : curr));
  };

  const removeLink = (linkId) => {
    setLinks((prev) => prev.filter((l) => l._id !== linkId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-paper border border-bordered rounded-2xl overflow-hidden p-4 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.18),0_10px_24px_-8px_rgba(15,23,42,0.08)] ring-1 ring-black/5">
      {/* When no rows are visible yet we still surface the New button
above any empty/feature-locked card. The DataTable header
(search + buttons) only renders when there are rows. */}
      {(maxLinks === 0 || links.length === 0) && (
        <div className="flex items-center justify-end mb-4">
          <Button
            variant="primary_s"
            onClick={() =>
              canCreate ? setShowCreate(true) : openGoPremium(upgradeReason())
            }
          >
            <PlusIcon className="w-4 h-4" strokeWidth={2} />
            {texts?.newLink}
          </Button>
        </div>
      )}

      {maxLinks === 0 && (
        <div className="bg-canvas border border-bordered border-dashed rounded-xl p-6 text-center">
          <Typography variant="txt_dim" className="block mb-3">
            {texts?.featureGatedNote}
          </Typography>
          <div className="flex justify-center">
            <Button
              variant="primary_s"
              onClick={() => openGoPremium(upgradeReason())}
            >
              {texts?.upgrade}
            </Button>
          </div>
        </div>
      )}

      {links.length === 0 && maxLinks !== 0 ? (
        <div className="bg-canvas border border-bordered border-dashed rounded-xl p-10 text-center space-y-3">
          <InboxArrowDownIcon className="w-10 h-10 text-text_secondary mx-auto mb-4" />
          <Typography variant="txt_dim">
            {texts?.noLinksNote}
          </Typography>
        </div>
      ) : links.length > 0 ? (
        <div ref={tableWrapperRef} className="relative">
          <DataTable
          data={links}
          searchField="name token"
          itemsPerPage={20}
          preferredHeight="auto"
          renders={[
            {
              id: "_id",
              title: (
                <Checkbox
                  variant="transfer"
                  isOn={allSelected}
                  onChange={toggleSelectAll}
                />
              ),
              headerClassName: "w-[40px]",
              render: (id) => (
                <Checkbox
                  variant="transfer"
                  isOn={selected.has(id)}
                  onChange={() => toggleSelect(id)}
                />
              ),
            },
            {
              id: "name",
              title: texts?.title,
              headerClassName: "min-w-[260px]",
              render: (_, link) => (
                <div
                  className="flex items-center gap-4 min-w-0 cursor-pointer"
                  onClick={() => setDrawerLink(link)}
                >
                  {link.status !== "expired" && (
                    <Button
                      variant="secondary_s"
                      onClick={(e) => copy(e, link)}
                      title={texts?.getLink}
                      className="px-2"
                    >
                      {copiedToken === link.token ? (
                        <>
                          <CheckIcon className="h-4 w-4 text-primary" />
                          <span className="text-primary">
                            {texts?.copied}
                          </span>
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="h-4 w-4" />
                          <span>{texts?.getLink}</span>
                        </>
                      )}
                    </Button>
                  )}
                  <div className="flex flex-col gap-1 min-w-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDrawerLink(link);
                      }}
                      className="font-medium text-sm w-full text-left text-link hover:underline truncate"
                    >
                      {link.name || texts?.untitledLink}
                    </button>
                    {(() => {
                      const tr = linkTimeRemaining(link);
                      if (!tr) return null;
                      return (
                        <div className="flex items-center gap-2 text-xs text-text_secondary">
                          <span>
                            <strong>{tr.value}</strong> {tr.suffix}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ),
            },
            {
              id: "transfers_used",
              title: texts?.uploads,
              headerClassName: "w-[100px]",
              render: (count, link) => (
                <span className="flex items-center gap-1 text-sm">
                  <InboxArrowDownIcon className="h-4 w-4 text-text_secondary flex-shrink-0" />
                  {count || 0}
                  {link.max_transfers ? `/${link.max_transfers}` : ""}
                </span>
              ),
            },
            {
              id: "created",
              title: texts?.date,
              headerClassName: "w-[100px]",
              render: (d) =>
                renderDateCell(
                  d,
                  texts?.today,
                  texts?.yesterday,
                ),
            },
            {
              id: "status",
              title: texts?.status,
              headerClassName: "w-[110px]",
              render: (_, link) => (
                <StatusBadge status={effectiveStatus(link)} />
              ),
            },
            {
              id: "actions",
              title: "",
              headerClassName: "w-[60px]",
              render: (_, link) => (
                <button
                  onClick={() => handleDeleteSingle(link)}
                  disabled={isDeleting}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-error hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
                  title={texts?.delete}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              ),
            },
          ]}
          itemIdField="_id"
          itemHeight="h-auto min-h-[64px]"
          styleOverrides={{
            header: "flex flex-wrap sm:flex-nowrap flex-row justify-left items-center mb-2 gap-2",
            searchInput: "ml-0 w-full sm:w-[340px] lg:w-[480px]",
            headerControls: "w-full sm:w-auto sm:flex-grow flex justify-end",
            tableWrapper: "h-auto flex-col overflow-y-auto overflow-x-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
            table: "w-full table-auto",
            headerRow:
              "text-left text-text text-sm md:text-base xl:text-[18px] font-semibold align-top bg-neutral_1/40 border-b border-bordered",
            getHeaderCellStyle: (headerClassName) =>
              `px-4 py-4 ${headerClassName || ""}`,
            getCellStyle: (itemClassName) =>
              `px-4 py-4 border-b border-neutral_1/50 ${itemClassName || ""}`,
          }}
          searchPlaceholder={
            texts?.searchLinksPh
          }
          headerControls={
            <div className="flex items-center gap-2">
              {someSelected && (
                <Button
                  variant="secondary_red_s"
                  onClick={handleDeleteSelected}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Spinner className="w-[16px] h-[16px]" />
                  ) : (
                    <span className="flex items-center gap-1">
                      <TrashIcon className="h-4 w-4" />
                      {texts?.delete} ({selected.size})
                    </span>
                  )}
                </Button>
              )}
              <Button
                variant="primary_s"
                onClick={() =>
                  canCreate
                    ? setShowCreate(true)
                    : openGoPremium(upgradeReason())
                }
              >
                <PlusIcon className="w-4 h-4" strokeWidth={2} />
                {texts?.newLink}
              </Button>
            </div>
          }
          tableHeader={<div className="h-2" />}
          noData={
            <Typography variant="txt_dim" className="block text-center py-8">
              {texts?.noLinksFound}
            </Typography>
          }
        />
          {showScrollHint && (
            <div
              ref={el => { scrollHintRef.current = el; if (el) positionScrollHint(); }}
              className="md:hidden pointer-events-none absolute right-2 z-20"
            >
              <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
                <ChevronRightIcon className="w-4 h-4 text-white" />
              </div>
            </div>
          )}
        </div>
      ) : null}

      {showCreate && (
        <ReceiveLinkDialog
          link={null}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}

      <ReceiveLinkDrawer
        link={drawerLink}
        onClose={() => setDrawerLink(null)}
        onChanged={upsertLink}
        onDeleted={removeLink}
      />
    </div>
  );
}
